import { useEffect, useMemo, useRef, useState } from "react";

import { AppButton, AppDialogSurface, AppText } from "../../ui-foundation";

const CROP_FRAME_SIZE = 280;
const OUTPUT_SIZE = 500;

type Position = {
  x: number;
  y: number;
};

interface AvatarCropDialogProps {
  open: boolean;
  imageSrc: string | null;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (blob: Blob) => Promise<void> | void;
}

const AvatarCropDialog = ({
  open,
  imageSrc,
  saving = false,
  onCancel,
  onConfirm,
}: AvatarCropDialogProps) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [processing, setProcessing] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origin: Position;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setImageSize(null);
    setProcessing(false);
  }, [open, imageSrc]);

  const baseScale = useMemo(() => {
    if (!imageSize) return 1;
    return Math.max(CROP_FRAME_SIZE / imageSize.width, CROP_FRAME_SIZE / imageSize.height);
  }, [imageSize]);

  const renderedSize = useMemo(() => {
    if (!imageSize) {
      return { width: CROP_FRAME_SIZE, height: CROP_FRAME_SIZE };
    }
    return {
      width: imageSize.width * baseScale * zoom,
      height: imageSize.height * baseScale * zoom,
    };
  }, [baseScale, imageSize, zoom]);

  const clampPosition = (next: Position) => {
    const maxX = Math.max(0, (renderedSize.width - CROP_FRAME_SIZE) / 2);
    const maxY = Math.max(0, (renderedSize.height - CROP_FRAME_SIZE) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  };

  useEffect(() => {
    setPosition((current) => clampPosition(current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedSize.width, renderedSize.height]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!imageSize) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: position,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    setPosition(clampPosition({
      x: dragRef.current.origin.x + deltaX,
      y: dragRef.current.origin.y + deltaY,
    }));
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleConfirm = async () => {
    if (!imageSrc || !imageSize) return;
    setProcessing(true);
    try {
      const image = new Image();
      image.src = imageSrc;
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error("Could not load the selected image."));
      });

      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;

      const context = canvas.getContext("2d");
      if (!context) throw new Error("Could not prepare the cropped image.");

      const scaledWidth = imageSize.width * baseScale * zoom;
      const scaledHeight = imageSize.height * baseScale * zoom;
      const left = (CROP_FRAME_SIZE - scaledWidth) / 2 + position.x;
      const top = (CROP_FRAME_SIZE - scaledHeight) / 2 + position.y;
      const exportScale = OUTPUT_SIZE / CROP_FRAME_SIZE;

      context.drawImage(
        image,
        left * exportScale,
        top * exportScale,
        scaledWidth * exportScale,
        scaledHeight * exportScale,
      );

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((value) => {
          if (value) {
            resolve(value);
            return;
          }
          reject(new Error("Could not create the cropped image."));
        }, "image/jpeg", 0.9);
      });

      await onConfirm(blob);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AppDialogSurface
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving && !processing) {
          onCancel();
        }
      }}
      dismissible={!saving && !processing}
      title="Adjust your photo"
      description="Drag to reposition and use zoom until the framing feels right in the avatar circle."
      footer={
        <>
          <AppButton type="button" tone="outline" onPress={onCancel} disabled={saving || processing}>
            Cancel
          </AppButton>
          <AppButton type="button" onPress={() => void handleConfirm()} disabled={!imageSrc || saving || processing}>
            {saving || processing ? "Saving..." : "Save photo"}
          </AppButton>
        </>
      }
    >
      <div className="space-y-4">
          <div className="flex justify-center">
            <div
              className="relative overflow-hidden rounded-full border border-border/80 bg-muted/40 shadow-inner"
              style={{ width: CROP_FRAME_SIZE, height: CROP_FRAME_SIZE }}
            >
              <div
                className="absolute inset-0 cursor-grab active:cursor-grabbing"
                style={{ touchAction: "none" }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt="Avatar crop preview"
                    onLoad={(event) =>
                      setImageSize({
                        width: event.currentTarget.naturalWidth,
                        height: event.currentTarget.naturalHeight,
                      })
                    }
                    draggable={false}
                    className="pointer-events-none absolute left-1/2 top-1/2 select-none"
                    style={{
                      width: renderedSize.width,
                      height: renderedSize.height,
                      transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
                      maxWidth: "none",
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <AppText className="font-medium" fontSize="$sm" lineHeight="$sm">
                Zoom
              </AppText>
              <AppButton
                type="button"
                tone="ghost"
                size="sm"
                onPress={() => {
                  setZoom(1);
                  setPosition({ x: 0, y: 0 });
                }}
              >
                Reset
              </AppButton>
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              className="w-full accent-[hsl(335_33%_42%)]"
            />
          </div>
      </div>
    </AppDialogSurface>
  );
};

export default AvatarCropDialog;
